import React, { type JSX } from 'react';

import { AutoCompleteTags } from 'pages/AddDevice';
import { FormHelperText, Stack } from '@mui/material';
import { useGetTagsQuery } from '@fiji/common/src/features/TagManagement/tagManagementApi';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { TAGS_LIMIT } from '@fiji/common/src/constants';
import { setUpdateProperties } from '@fiji/common/src/features/deviceManagement/deviceSlice';
import { CustomTransComponent } from 'components';

type UpdateTagsModalProps = {
    data: any;
    createHandler: any;
};

const getPayloadTags = (propertiesPayload: any): string[] => propertiesPayload?.tags ?? [];

const getTagNames = (data: any): string[] => data?.tags?.map((item: any) => item?.name) ?? [];

const getTagOptions = (deviceTagsList: any): string[] =>
    deviceTagsList?.data?.records?.map((tag: any) => tag?.name) ?? [];

const isNavigationKey = (code: string): boolean => code === 'ArrowDown' || code === 'ArrowUp';

const canAddEnteredTag = (e: any, isEnteredTagValue: boolean, currentTagCount: number): boolean =>
    !isEnteredTagValue && e.code === 'Enter' && currentTagCount < TAGS_LIMIT;

const clonePayloadWithTag = (propertiesPayload: any, tagValue: string): any => {
    const propertiesClone = JSON.parse(JSON.stringify(propertiesPayload));
    propertiesClone.tags = [...(propertiesClone.tags ?? []), tagValue];
    return propertiesClone;
};

const updateTags = (dispatch: any, tags: string[]): void => {
    dispatch(setUpdateProperties({ key: 'tags', value: tags }));
};

const submitEnteredTag = async (
    e: any,
    isEnteredTagValue: boolean,
    propertiesPayload: any,
    deviceTagsList: any,
    createHandler: any,
    dispatch: any
): Promise<void> => {
    const currentTags = getPayloadTags(propertiesPayload);
    if (!canAddEnteredTag(e, isEnteredTagValue, currentTags.length)) {
        return;
    }

    const tagValue = e.target.value;
    const selectedTag = deviceTagsList?.data?.records?.find((item: any) => item?.name === tagValue);
    const propertiesClone = clonePayloadWithTag(propertiesPayload, tagValue);

    if (selectedTag) {
        updateTags(dispatch, propertiesClone?.tags);
        return;
    }

    const { error }: any = await createHandler({ name: tagValue });
    if (!error) {
        updateTags(dispatch, propertiesClone?.tags);
    }
};

export const UpdateTagsModal = ({ data, createHandler }: UpdateTagsModalProps): JSX.Element => {
    const {
        data: deviceTagsList,
        isLoading: isDeviceTagLoading,
        isFetching: isDeviceTagFetching,
    } = useGetTagsQuery<{
        data: any;
        isLoading: boolean;
        isFetching: boolean;
    }>({
        page: 0,
        size: 100,
    });

    const dispatch = useAppDispatch();
    const { propertiesPayload } = useTypedSelector((state) => state.device);

    const selectedTags = getPayloadTags(propertiesPayload);
    const tagOptions = getTagOptions(deviceTagsList);
    const selectedTagCount = selectedTags.length;

    React.useEffect(() => {
        if (!data?.isDeviceTagUpdate) {
            return;
        }

        updateTags(dispatch, getTagNames(data));
    }, [data]);

    const selectedTagsHandler = (e: any, values: any): void => {
        if (values?.length > TAGS_LIMIT) {
            return;
        }

        updateTags(dispatch, values);
    };

    let isEnteredTagValue = false;
    const handleKeyDown = async (e: any): Promise<void> => {
        if (isNavigationKey(e.code)) {
            isEnteredTagValue = true;
        } else if (e.code !== 'Enter' && !isNavigationKey(e.code)) {
            isEnteredTagValue = false;
        }

        await submitEnteredTag(e, isEnteredTagValue, propertiesPayload, deviceTagsList, createHandler, dispatch);
    };

    const handleTagDelete = (_tag: any, index: number): void => {
        const propertiesClone = JSON.parse(JSON.stringify(propertiesPayload));
        propertiesClone.tags.splice(index, 1);
        updateTags(dispatch, propertiesClone?.tags);
    };

    return (
        <>
            <AutoCompleteTags
                value={selectedTags}
                disabled={isDeviceTagFetching || isDeviceTagLoading}
                onChange={selectedTagsHandler}
                options={tagOptions}
                handleTagDelete={handleTagDelete}
                onKeyDown={handleKeyDown}
                isClearAll={true}
            />
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                <FormHelperText className="padding-x-16">
                    <CustomTransComponent translationKey={'COMMON:MAX_OF_6_TAGS'} />
                </FormHelperText>
                <FormHelperText className="padding-x-16">{selectedTagCount} /6</FormHelperText>
            </Stack>
        </>
    );
};
